import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";

import { trackEvent } from "../lib/api";
import AuthoritySection from "../components/AuthoritySection";
import CaseStudies from "../components/CaseStudies";
import ReviewBadge from "../components/ReviewBadge";
import WebsiteCTA from "../components/WebsiteCTA";
import { BRANDS, TYPEFORM_ID, TYPEFORM_FALLBACK_URL } from "../data/content";

const BRAND_LINE =
  BRANDS.slice(0, -1).join(", ") + ", & " + BRANDS[BRANDS.length - 1];

export default function Apply() {
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    trackEvent("apply_page_view");
    const SRC = "//embed.typeform.com/next/embed.js";
    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      if (window.tf && typeof window.tf.load === "function") window.tf.load();
      return;
    }
    const script = document.createElement("script");
    script.src = SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const el = document.querySelector('[data-testid="casestudies-grid-section"]');
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowApply(entry.isIntersecting || entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scrollToForm = () => {
    trackEvent("sticky_apply_click");
    document
      .querySelector('[data-testid="typeform-section"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ===== Announcement banner (matches www.DSAeCommerce.com) ===== */}
      <div
        className="w-full bg-brand px-5 py-4 text-center"
        data-testid="top-banner"
      >
        <p className="mx-auto max-w-4xl font-bold leading-snug text-white text-sm md:text-base">
          7+ Years in Business&nbsp;&nbsp;|&nbsp;&nbsp;400+ Clients&nbsp;&nbsp;|&nbsp;&nbsp;Official Amazon &amp; Walmart Agency Partner
        </p>
      </div>

      {/* ===== Application content ===== */}
      <main className="mx-auto max-w-3xl px-5 pb-3 pt-6 md:pt-8">
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center text-3xl font-black leading-[1.03] tracking-tight text-brand-ink sm:text-4xl md:text-5xl"
          data-testid="apply-headline"
        >
          Now Accepting Clients for 2026 Q4 — Limited Spots
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-5 flex items-center justify-center gap-2"
        >
          <p className="text-base font-semibold text-brand-ink underline decoration-2 underline-offset-4 md:text-lg">
            Fill in the 60-second application below
          </p>
          <ArrowDown className="h-5 w-5 animate-bounce text-brand" aria-hidden="true" />
        </motion.div>
      </main>

      {/* ===== Typeform (questions render inline, tall enough to show all choices) ===== */}
      <section
        className="mx-auto max-w-5xl px-5 pb-10"
        data-testid="typeform-section"
      >
        <div
          className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
          data-testid="typeform-embed-wrapper"
        >
          <div data-tf-live={TYPEFORM_ID} className="tf-embed" />
        </div>
        <p className="mt-4 text-center text-sm text-neutral-500">
          Trouble loading the form?{" "}
          <a
            href={TYPEFORM_FALLBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand underline underline-offset-4 transition-opacity hover:opacity-80"
            data-testid="typeform-fallback-link"
          >
            Open it in a new tab
          </a>
          .
        </p>
      </section>

      {/* ===== 5-star reviews (moved up, right under the form) ===== */}
      <div className="bg-white px-5 pb-12">
        <ReviewBadge />
      </div>

      {/* ===== Authority: official partner badges + stats ===== */}
      <AuthoritySection />

      {/* ===== Authorized brands (moved below the questions) ===== */}
      <section className="mx-auto max-w-3xl px-5 pb-16 text-center" data-testid="brands-section">
        <div className="mx-auto mb-8 h-px w-24 bg-neutral-200" />
        <h2 className="text-xl font-bold text-brand md:text-2xl" data-testid="brands-heading">
          Authorized Q4 Brands for Amazon &amp; Walmart:
        </h2>
        <p
          className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-neutral-800 md:text-lg"
          data-testid="brands-list"
        >
          {BRAND_LINE}
        </p>
      </section>

      {/* ===== Case studies: 50 real client dashboards ===== */}
      <CaseStudies />

      {/* ===== Visit main website ===== */}
      <WebsiteCTA />

      <footer className="bg-obsidian px-5 py-8 text-center">
        <p className="text-xs text-neutral-500">
          © {new Date().getFullYear()} DSAeCommerce. Brand names are property of their respective owners.
        </p>
      </footer>

      {/* ===== Sticky "Apply Now" (appears at case studies) ===== */}
      <AnimatePresence>
        {showApply && (
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={scrollToForm}
            data-testid="sticky-apply-btn"
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-black uppercase tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(79,87,214,0.7)] transition-colors duration-200 hover:bg-brand-dark"
          >
            <ArrowUp className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
            Apply Now
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
